import { ModalProvider } from "@/components/modal";
import { Loader } from "@/components/display";
import { Permiso } from "@/shared/types/permiso";
import { PropsWithChildren, useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router";
import RequirePermisos from "./require-permisos";
import { useOperationalMode } from "@/features/configuration/hooks/use-operational-mode";
import { useAuthContext } from "@/features/auth/contexts/auth-context";
import ErrorPage from "@/pages/error";

interface PageProps extends PropsWithChildren {
    permisos?: Permiso[];
    requireGestion?: boolean;
    requireFacturacion?: boolean;
}

export default function Page({
    permisos,
    requireGestion,
    requireFacturacion,
    children
}: PageProps) {
    const { pathname } = useLocation();
    const { isLoggedIn, isLogingIn } = useAuthContext();
    const { gestionEnabled, facturacionEnabled } = useOperationalMode();
    const [isReady, setIsReady] = useState(false);

    // Esperar a que la autenticación esté completamente lista
    useEffect(() => {
        if (isLoggedIn && !isLogingIn) {
            // Pequeño delay para asegurar que los headers de axios estén actualizados
            const timer = setTimeout(() => setIsReady(true), 100);
            return () => clearTimeout(timer);
        } else {
            setIsReady(false);
        }
    }, [isLoggedIn, isLogingIn]);

    // Mostrar loader mientras se autentica o se preparan los headers
    if (isLogingIn || !isReady) {
        return <Loader height="100vh" />;
    }

    // Validar que esté autenticado
    if (!isLoggedIn) {
        return <ErrorPage.Content status={401} title={"No autenticado"} message={"Debes estar autenticado para acceder a esta página."} />;
    }

    if (requireGestion && !gestionEnabled) {
        return <ErrorPage.Content status={403} title={"Modo no habilitado"} message={"Esta sección requiere modo Gestión activo."} />;
    }

    if (requireFacturacion && !facturacionEnabled) {
        return <ErrorPage.Content status={403} title={"Modo no habilitado"} message={"Esta sección requiere modo Facturación activo."} />;
    }

    if (!permisos) {
        return <ModalProvider key={pathname}>
            {children ? children : <Outlet />}
        </ModalProvider>
    }

    return <RequirePermisos permisos={permisos}>
        <ModalProvider key={pathname}>
            {children ? children : <Outlet />}
        </ModalProvider>
    </RequirePermisos>
}